/**
 * ProductVariantModal Web Component
 *
 * Reusable modal for selecting product variants.
 * Used for wishlist editing and GWP Product quick view.
 *
 * APIs Used:
 * - Shopify Storefront API (GraphQL): Fetch product data including variants, prices, images
 *
 * Methods:
 * - open(options): Open modal with product data and configuration
 * - close(): Close modal and cleanup
 * - initializeCarousel(): Initialize Splide carousel for product images
 * - renderModalContent(productData, selectedVariant, config): Generate modal HTML
 * - initializeVariantPicker(productData, config): Render variant selectors with color swatches
 * - updateVariantAvailability(productData): Mark unavailable variant options visually
 * - updatePrice(productData): Update price when variant selection changes
 * - updatePrimaryButton(productData, config): Enable/disable primary button based on variant availability
 * - getSelectedOptions(): Get currently selected options from modal buttons
 * - findVariantByOptions(productData, selectedOptions): Find variant matching selected options
 * - getColorSwatchConfig(): Get color swatch configuration from data attributes
 * - getColorSwatchStyle(): Get color swatch style (round/rectangle) from data attributes
 * - renderColorSwatch(value, index, config, style): Render color variant as swatch
 * - formatPrice(amount, currencyCode): Format price using Intl.NumberFormat
 */

class ProductVariantModal extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.overlay = null;
    this.currentProductData = null;
    this.currentConfig = null;
  }

  connectedCallback() {
    this.modal = this.querySelector('.product-variant-modal');
    this.overlay = this.querySelector('.product-variant-modal__overlay');

    // Add close button handlers
    this.querySelectorAll('[data-variant-modal-close]').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
  }

  /**
   * Open modal with product data and configuration
   * @param {Object} options - Configuration object
   * @param {Object} options.productData - Product data from Storefront API
   * @param {Object} options.selectedVariant - Currently selected variant
   * @param {String} options.productUrl - Product page URL
   * @param {String} options.mode - 'wishlist' or 'gwp'
   * @param {Function} options.onSave - Callback for primary button (save/add to cart)
   * @param {Object} options.context - Additional context data passed to callback
   */
  async open(options) {
    const {
      productData,
      selectedVariant,
      productUrl,
      hasSizeGuide,
      mode = 'wishlist',
      onSave,
      context = {}
    } = options;

    this.currentProductData = productData;
    this.currentConfig = {
      mode,
      onSave,
      context,
      productUrl,
      selectedVariant,
      hasSizeGuide
    };

    // Render modal content
    const modalContent = this.modal.querySelector('#product-variant-modal__inner');
    if (!modalContent) return;

    modalContent.innerHTML = this.renderModalContent(productData, selectedVariant, {
      mode,
      productUrl
    });

    // Open modal
    this.openModal();

    // Initialize carousel and variant picker after render
    requestAnimationFrame(() => {
      this.initializeCarousel();
      this.initializeVariantPicker(productData, {
        mode,
        onSave,
        context
      });
    });
  }

  initializeCarousel() {
    const carouselEl = this.modal.querySelector('#modal-carousel');
    if (!carouselEl || typeof Splide === 'undefined') return;

    // https://splidejs.com/guides/options/
    this.carousel = new Splide(carouselEl, {
      type: 'slide',
      speed: 400,
      perPage: 1,
      gap: '0.4rem',
      pagination: false,
      padding: {
        right: '5rem'
      },
      mediaQuery: 'min',
      breakpoints: {
        1024: {
          padding: {
            right: '20rem'
          }
        },
      }
    });

    this.carousel.mount();
  }

  renderModalContent(productData, selectedVariant, config) {
    const price = selectedVariant?.price || productData.priceRange?.minVariantPrice;
    const compareAtPrice = selectedVariant?.compareAtPrice;
    const hasDiscount = compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(price.amount);
    const { mode, productUrl } = config;
    const images = productData.images?.nodes || [];
    const buttonText = mode === 'gwp' ? 'ADD TO CART' : 'SAVE SIZE';
    const buttonId = mode === 'gwp' ? 'add-gwp-to-cart' : 'save-wishlist-changes';

    // TODO: Update heading to dynamically display "Select ____" based on available variants.
    return `
      <div class="product-variant-modal__head">
      <h4>${mode === 'gwp' ? 'Select Size' : 'Edit Product'}</h4>
      </div>

      <div class="product-variant-modal__product">
        <div class="product-variant-modal__gallery">
          ${images.length > 0 ? `
            <div class="modal-carousel splide" id="modal-carousel">
              <div class="splide__track">
                <div class="splide__list">
                  ${images.map((image, index) => `
                    <div class="splide__slide">
                      <img
                        src="${image.url}"
                        alt="${image.altText || productData.title}"
                        width="${image.width || 600}"
                        height="${image.height || 600}"
                        loading="${index === 0 ? 'eager' : 'lazy'}"
                      />
                    </div>
                  `).join('')}
                </div>
              </div>
            </div>
          ` : ''}
        </div>

        <div class="product-variant-modal__info">
          <h3 class="product-variant-modal__title">${productData.title}</h3>

          <div class="product-variant-modal__price text-size-medium">
            ${hasDiscount ? `
              <s class="price-item price-item--regular">${this.formatPrice(compareAtPrice.amount, compareAtPrice.currencyCode)}</s>
              <span class="price-item price-item--sale">${this.formatPrice(price.amount, price.currencyCode)}</span>
            ` : `
              <span class="price-item price-item--regular">
                ${price ? this.formatPrice(price.amount, price.currencyCode) : ''}
              </span>
            `}
          </div>

          ${this.renderBadges(productData, selectedVariant)}

          <div class="product-variant-modal__variants" id="variant-selectors">
          </div>
        </div>

        <div class="product-variant-modal__actions">
          <button class="btn btn-primary" id="${buttonId}">
            ${buttonText}
          </button>
          <a href="${productUrl}" class="product-variant-modal__view-product">
            View Product Details
          </a>
        </div>
      </div>
    `;
  }

  initializeVariantPicker(productData, config) {
    const variantContainer = this.modal.querySelector('#variant-selectors');
    if (!variantContainer || !productData.options) return;

    const colorSwatchConfig = this.getColorSwatchConfig();
    const colorSwatchStyle = this.getColorSwatchStyle();

    productData.options.forEach((option, index) => {
      if (option.name === 'Title') return;

      const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';

      const optionHTML = `
        <div class="variant-option" data-option-index="${index}">
          <div class="variant-option__head">
            <label class="variant-option__label" data-option-name="${option.name}">
              <span class="variant-option__label-name">${option.name}:</span>
              <span class="variant-option__label-value"></span>
            </label>
            ${!isColorOption && this.currentConfig.hasSizeGuide ? `
              <modal-opener data-modal="#SizeguideModal-variant-modal">
                <button
                  class="product-popup-modal__button text-link text-size-small link inline-flex gap-x-2 items-center"
                  type="button"
                  aria-haspopup="dialog"
                >
                  Size Guide
                </button>
              </modal-opener>
            ` : ''}
          </div>
          <div class="variant-option__values ${isColorOption ? 'variant-option__values--color' : ''}">
            ${option.values.map(value => {
              if (isColorOption) {
                return this.renderColorSwatch(value, index, colorSwatchConfig, colorSwatchStyle);
              } else {
                return `
                  <button
                    class="variant-option__value"
                    data-option-index="${index}"
                    data-option-value="${value}"
                    type="button"
                  >
                    ${value}
                  </button>
                `;
              }
            }).join('')}
          </div>
        </div>
      `;

      variantContainer.insertAdjacentHTML('beforeend', optionHTML);
    });

    // Set the active options
    const selectedVariant = this.currentConfig.selectedVariant || productData.variants.nodes.find(v => v.availableForSale);
    if (selectedVariant) {
      selectedVariant.selectedOptions.forEach((option) => {
        const button = variantContainer.querySelector(
          `[data-option-value="${option.value}"]`
        );
        if (button) {
          button.classList.add('active');
        }

        // Update label with selected value
        this.updateOptionLabel(option.name, option.value);
      });
    }

    variantContainer.querySelectorAll('.variant-option__value, .variant-option__value--swatch').forEach(button => {
      button.addEventListener('click', () => {
        const index = button.dataset.optionIndex;
        const optionValue = button.dataset.optionValue;
        const siblings = variantContainer.querySelectorAll(`[data-option-index="${index}"]`);

        siblings.forEach(sib => sib.classList.remove('active'));
        button.classList.add('active');

        const variantOption = button.closest('.variant-option');
        const optionName = variantOption.querySelector('.variant-option__label').dataset.optionName;
        this.updateOptionLabel(optionName, optionValue);

        this.updateVariantAvailability(productData);
        this.updatePrice(productData);
        this.updatePrimaryButton(productData, config);
      });
    });

    this.updateVariantAvailability(productData);
    this.updatePrimaryButton(productData, config);

    const primaryButton = this.modal.querySelector(`#${config.mode === 'gwp' ? 'add-gwp-to-cart' : 'save-wishlist-changes'}`);
    if (primaryButton && config.onSave) {
      primaryButton.addEventListener('click', () => {
        const selectedOptions = this.getSelectedOptions();
        const variant = this.findVariantByOptions(productData, selectedOptions);

        if (variant && config.onSave) {
          config.onSave({
            variant,
            productData,
            context: config.context
          });
        }
      });
    }
  }

  updateVariantAvailability(productData) {
    const variantContainer = this.modal.querySelector('#variant-selectors');
    if (!variantContainer) return;

    // Get currently selected options
    const selectedOptions = [];
    productData.options.forEach((option, index) => {
      if (option.name === 'Title') return;
      const activeButton = variantContainer.querySelector(`[data-option-index="${index}"].active`);
      selectedOptions[index] = activeButton ? activeButton.dataset.optionValue : null;
    });

    // Check each option value for availability
    productData.options.forEach((option, optionIndex) => {
      if (option.name === 'Title') return;

      option.values.forEach(value => {
        const button = variantContainer.querySelector(
          `[data-option-index="${optionIndex}"][data-option-value="${value}"]`
        );
        if (!button) return;

        const testOptions = [...selectedOptions];
        testOptions[optionIndex] = value;

        // Check if any variant exists with these options and is available
        const isAvailable = productData.variants.nodes.some(variant => {
          // Check if this variant matches the test options
          const matches = variant.selectedOptions.every((opt, idx) => {
            // For the current option being tested, must match
            if (idx === optionIndex) return opt.value === value;
            // For other selected options, must match if selected
            if (testOptions[idx]) return opt.value === testOptions[idx];
            // If no selection for this option yet, don't filter by it
            return true;
          });

          return matches && variant.availableForSale;
        });

        // Update disabled state visually but allow clicking
        if (isAvailable) {
          button.classList.remove('disabled');
        } else {
          button.classList.add('disabled');
        }
      });
    });
  }

  updateOptionLabel(optionName, optionValue) {
    const label = this.modal.querySelector(`.variant-option__label[data-option-name="${optionName}"]`);
    if (!label) return;

    const labelValue = label.querySelector('.variant-option__label-value');
    if (labelValue) {
      labelValue.textContent = optionValue;
    }
  }

  updatePrimaryButton(productData, config) {
    const primaryButton = this.modal.querySelector(`#${config.mode === 'gwp' ? 'add-gwp-to-cart' : 'save-wishlist-changes'}`);
    if (!primaryButton) return;

    // Get selected variant
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariantByOptions(productData, selectedOptions);

    // Disable primary button if variant is unavailable
    if (variant && variant.availableForSale) {
      primaryButton.disabled = false;
    } else {
      primaryButton.disabled = true;
    }
  }

  updatePrice(productData) {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariantByOptions(productData, selectedOptions);

    if (variant) {
      const priceElement = this.modal.querySelector('.product-variant-modal__price');
      if (priceElement) {
        const price = variant.price;
        const compareAtPrice = variant.compareAtPrice;
        const hasDiscount = compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(price.amount);

        priceElement.innerHTML = hasDiscount ? `
          <s class="price-item price-item--regular">
            ${this.formatPrice(compareAtPrice.amount, compareAtPrice.currencyCode)}
          </s>
          <span class="price-item price-item--sale">
            ${this.formatPrice(price.amount, price.currencyCode)}
          </span>
        ` : `
          <span class="price-item price-item--regular">
            ${this.formatPrice(price.amount, price.currencyCode)}
          </span>
        `;
      }

      // Update badges
      const badgeContainer = this.modal.querySelector('.product-variant-modal__badge');
      const newBadges = this.renderBadges(productData, variant);

      if (badgeContainer && newBadges) {
        badgeContainer.outerHTML = newBadges;
      } else if (!badgeContainer && newBadges) {
        // Insert badges after price
        priceElement.insertAdjacentHTML('afterend', newBadges);
      } else if (badgeContainer && !newBadges) {
        badgeContainer.remove();
      }
    }
  }

  getSelectedOptions() {
    const options = [];
    const activeButtons = this.modal.querySelectorAll('.variant-option__value.active, .variant-option__value--swatch.active');

    activeButtons.forEach(button => {
      options.push({
        index: parseInt(button.dataset.optionIndex),
        value: button.dataset.optionValue
      });
    });

    return options.sort((a, b) => a.index - b.index).map(o => o.value);
  }

  findVariantByOptions(productData, selectedOptions) {
    return productData.variants.nodes.find(variant => {
      return variant.selectedOptions.every((option, index) => {
        return option.value === selectedOptions[index];
      });
    });
  }

  getColorSwatchConfig() {
    const configText = this.dataset.colorSwatchConfig;
    if (!configText) return {};

    const config = {};
    configText.split('\n').forEach(line => {
      const parts = line.split(':');
      if (parts.length >= 2) {
        const name = parts[0].trim().toLowerCase();
        const value = parts.slice(1).join(':').trim();
        config[name] = value;
      }
    });
    return config;
  }

  getColorSwatchStyle() {
    return this.dataset.colorSwatchStyle || 'round';
  }

  renderColorSwatch(value, index, colorSwatchConfig, colorSwatchStyle) {
    const valueLower = value.toLowerCase();
    let cssProperty = `--swatch-color: ${valueLower}`;

    // Check if there's a custom color config
    if (colorSwatchConfig[valueLower]) {
      const swatchValue = colorSwatchConfig[valueLower];
      if (swatchValue.includes('#')) {
        cssProperty = `--swatch-color: ${swatchValue}`;
      } else if (swatchValue.includes('http') || swatchValue.includes('.')) {
        cssProperty = `--swatch-color: url(${swatchValue})`;
      }
    }

    const roundedClass = colorSwatchStyle === 'round' ? 'rounded-full' : '';

    return `
      <button
        class="variant-option__value--swatch ${roundedClass}"
        data-option-index="${index}"
        data-option-value="${value}"
        type="button"
        title="${value}"
      >
        <span class="swatch-color ${roundedClass}" style="${cssProperty}">
          <span class="sr-only">${value}</span>
        </span>
      </button>
    `;
  }

  // Pretty much a duplicate of renderBadges from wishlist-product-grid
  renderBadges(productData, variant) {
    const badges = [];
    const badgeSettings = window.SHOPIFY_DATA?.badge_settings || {};

    const price = variant?.price || productData.priceRange?.minVariantPrice;
    const compareAtPrice = variant?.compareAtPrice;
    const isAvailable = variant?.availableForSale ?? productData.availableForSale;
    const hasDiscount = compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(price.amount);

    // Sold out badge
    if (!isAvailable && badgeSettings.showSoldOutBadge) {
      badges.push(`<span class="badge price__badge-sold-out">${badgeSettings.labelSoldOut || 'SOLD OUT'}</span>`);
    }
    // Discount badge
    else if (hasDiscount && isAvailable && badgeSettings.showDiscountBadge) {
      let badgeText = '';

      if (badgeSettings.saleBadgeType === 'text') {
        badgeText = badgeSettings.labelOnSale || 'ON SALE';
      } else {
        const discountValue = this.calculateDiscount(
          parseFloat(compareAtPrice.amount),
          parseFloat(price.amount),
          badgeSettings.saleBadgeType,
          price.currencyCode
        );
        badgeText = (badgeSettings.labelSave || 'SAVE [AMOUNT]').replace('[AMOUNT]', discountValue);
      }

      badges.push(`<span class="badge price__badge-sale">${badgeText}</span>`);
    }

    // Custom label badges (from product tags)
    if (badgeSettings.cardLabelText) {
      const labelTexts = badgeSettings.cardLabelText.split(',').map(t => t.trim().toLowerCase());
      const productTags = productData.tags || [];

      for (const tag of productTags) {
        const tagLower = tag.toLowerCase();
        for (const labelText of labelTexts) {
          if (tagLower === labelText) {
            badges.push(`<div class="badge product__badge-label">${labelText}</div>`);
            break;
          }
        }
      }
    }

    if (badges.length === 0) return '';

    return `<div class="product-variant-modal__badge card__badge">${badges.join('')}</div>`;
  }

  calculateDiscount(comparePrice, price, type, currencyCode) {
    if (type === 'percentage') {
      const percentage = ((comparePrice - price) / comparePrice * 100).toFixed(0);
      return `${percentage}%`;
    } else if (type === 'fixed') {
      const discount = comparePrice - price;
      return this.formatPrice(discount, currencyCode);
    }
    return '';
  }

  formatPrice(amount, currencyCode) {
    const numAmount = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'AUD',
      currencyDisplay: 'narrowSymbol'
    }).format(numAmount);
  }

  openModal() {
    document.body.classList.add('variant-modal-open');
    if (this.overlay) {
      this.overlay.classList.add('active');
    }

    requestAnimationFrame(() => {
      this.modal.classList.add('active');
    });
  }

  close() {
    // Destroy carousel
    if (this.carousel) {
      this.carousel.destroy();
      this.carousel = null;
    }

    this.modal.classList.remove('active');
    if (this.overlay) {
      this.overlay.classList.remove('active');
    }

    setTimeout(() => {
      document.body.classList.remove('variant-modal-open');

      // Clear modal content
      const modalContent = this.modal.querySelector('#product-variant-modal__inner');
      if (modalContent) {
        modalContent.innerHTML = '';
      }

      // Reset state
      this.currentProductData = null;
      this.currentConfig = null;
    }, 300); // Match transition duration
  }
}

customElements.define('product-variant-modal', ProductVariantModal);
